import { Typography } from '@mui/material';
import moment from 'moment';
import {
	AutocompleteArrayInput,
	Datagrid,
	DateField,
	Filter,
	FunctionField,
	NumberField,
	ReferenceArrayInput,
	ReferenceField,
	ReferenceInput,
	SelectInput,
	TextField,
	useListContext,
	usePermissions,
	useTranslate,
  AutocompleteInput,} from 'react-admin';
import { CheckChainUsesModule, ModuleKey, Permission, ReportList } from '../../components';
import { BetweenDatesInput } from '../../components/BetweenDatesInput';
import { CPFField } from '../../components/Fields';
import ListPagination from '../../components/ListPagination';
import { PERMISSIONS } from '../../constants';
import { getChainId, getPlaceId } from '../../lib';
import { filterToQuery } from '../../utils';

const titleSx = {
	margin: '10px 0 10px 0',
	textAlign: 'center',
	fontWeight: 'bold'
};

const statusLabels: Record<string, string> = {
	pending: 'Pendente',
	approved: 'Aprovado',
	failed: 'Falhou',
	Todos: 'Todos',
};

const translateStatus = (status: string) => statusLabels[status] || status;

const statusChoices = [
	{ id: 'pending', name: 'Pendente' },
	{ id: 'approved', name: 'Aprovado' },
	{ id: 'failed', name: 'Falhou' },
];

const DotzRefuelContent = () => {
	const { data } = useListContext();

	let detailData: any[] = [];
	let summaryData: any[] = [];

	if (data) {
		const reportInfo = data[0];
		if (reportInfo) {
			detailData = reportInfo.data;
			summaryData = reportInfo.summaryData;
		}
	}

	return (
		<>
			<Typography sx={titleSx}>Totais por Status</Typography>
			<Datagrid bulkActionButtons={false} style={{ marginBottom: 60 }} data={summaryData} total={1} hover={false}>
				<FunctionField
					textAlign="center"
					label="Status"
					sortable={false}
					render={(record: any) => translateStatus(record.status)}
				/>
				<NumberField
					source="total_transactions"
					label="Total de Transações"
					textAlign="center"
					emptyText="0"
					sortable={false}
				/>
				<NumberField
					source="total_value"
					label="Valor Total (Dotz)"
					textAlign="center"
					emptyText="0"
					sortable={false}
					options={{
						minimumFractionDigits: 2,
						maximumFractionDigits: 2,
					}}
				/>
				<NumberField
					source="total_value_in_reais"
					label="Valor Total (R$)"
					textAlign="center"
					emptyText="R$ 0,00"
					sortable={false}
					options={{
						style: 'currency',
						currency: 'BRL',
						minimumFractionDigits: 2,
						maximumFractionDigits: 2,
					}}
				/>
			</Datagrid>

			<Typography sx={titleSx}>Lista Detalhada</Typography>
			<Datagrid bulkActionButtons={false} data={detailData} hover={false}>
				<FunctionField
					textAlign="center"
					label="Status"
					sortable={false}
					render={(record: any) => translateStatus(record.status)}
				/>
				<ReferenceField
					label="Cliente"
					textAlign="center"
					emptyText="Não identificado"
					source="customer_id"
					reference="customers"
					link="show"
					sortable={false}
				>
					<TextField source="name" />
				</ReferenceField>
				<ReferenceField
					label="CPF"
					textAlign="center"
					emptyText="--"
					source="customer_id"
					reference="customers"
					link={false}
					sortable={false}
				>
					<CPFField source="cpf" />
				</ReferenceField>
				<ReferenceField
					label="Posto"
					textAlign="center"
					emptyText="Não identificado"
					source="place_id"
					reference="places"
					link="show"
					sortable={false}
				>
					<TextField source="name" />
				</ReferenceField>
				<ReferenceField
					label="Abastecimento"
					textAlign="center"
					emptyText="--"
					source="fillin_id"
					reference="fillins"
					link="show"
					sortable={false}
				>
					<FunctionField render={() => 'Abastecimento'} />
				</ReferenceField>
				<NumberField
					source="value"
					label="Valor (Dotz)"
					textAlign="center"
					emptyText="0"
					sortable={false}
					options={{
						minimumFractionDigits: 2,
						maximumFractionDigits: 2,
					}}
				/>
				<NumberField
					source="value_in_reais"
					label="Valor (R$)"
					textAlign="center"
					emptyText="R$ 0,00"
					sortable={false}
					options={{
						style: 'currency',
						currency: 'BRL',
						minimumFractionDigits: 2,
						maximumFractionDigits: 2,
					}}
				/>
				<Permission permission={PERMISSIONS.ADMIN}>
					<TextField source="dotz_transaction_id" label="ID Transação Dotz" textAlign="center" sortable={false} />
				</Permission>
				<DateField source="created_at" label="Data" showTime sortable={false} locales="pt-BR" />
			</Datagrid>
		</>
	);
};

const FilterTablePrint = () => {
	const { filterValues } = useListContext();
	return (
		<Datagrid bulkActionButtons={false} data={[{ ...filterValues, generatedReportAt: moment().toISOString() }]} total={1}>
			<DateField source="from" textAlign="center" label="De" sortable={false} locales="pt-BR" />
			<DateField source="to" textAlign="center" label="Até" sortable={false} locales="pt-BR" />
			<FunctionField
				label="Status"
				textAlign="center"
				sortable={false}
				render={(record: any) => record.status ? translateStatus(record.status) : 'Todos'}
			/>
			<DateField source="generatedReportAt" textAlign="center" label="Gerado às" showTime sortable={false} locales="pt-BR" />
		</Datagrid>
	);
};

const ReportDotzRefuelTransactions = (props: any) => {
	const { permissions } = usePermissions();
	const translate = useTranslate();
	const placeId = getPlaceId();

	const listFilters = [
		<BetweenDatesInput alwaysOn />,
		<SelectInput
			source="status"
			label="Status"
			choices={statusChoices}
			emptyText="Todos"
			style={{ minWidth: 180 }}
			alwaysOn
		/>,
	];

	if (!placeId) {
		listFilters.push(
			<ReferenceArrayInput
				source="stationIds"
				reference="places"
				filter={{ chainId: getChainId() }}
				perPage={null}
				sort={{ field: 'name', order: 'ASC' }}
				alwaysOn
			>
				<AutocompleteArrayInput
					filterToQuery={filterToQuery}
					label="Postos"
					sx={{ minWidth: 180, '& .MuiAutocomplete-inputRoot .MuiAutocomplete-input': { minWidth: 50 } }}
					optionText="name"
					noOptionsText={translate('ra.input.autocomplete.no_options_text')}
				/>
			</ReferenceArrayInput>
		);
	}

	if (permissions && permissions.includes(PERMISSIONS.ADMIN)) {
		listFilters.splice(1, 0,
			<ReferenceInput
				source="chainId"
				reference="chains"
				perPage={null}
				sort={{ field: 'name', order: 'ASC' }}
				alwaysOn
			>
				<AutocompleteInput
					filterToQuery={filterToQuery}
					style={{ minWidth: 180 }}
					optionText="name"
					label="Rede"
				/>
			</ReferenceInput>
		);
	}

	return (
		<CheckChainUsesModule module={ModuleKey.DOTZ}>
			<Permission permission={PERMISSIONS.DOTZ_REFUEL_TRANSACTIONS_REPORT}>
				<ReportList periodRequired
					{...props}
					title="Transações Dotz - Abastecimento"
					resource="chains/chainId/reports/dotz-refuel-transactions"
					filters={<Filter>{listFilters}</Filter>}
					sort={{ field: 'created_at', order: 'DESC' }}
					perPage={25}
					filterDefaultValues={{
						chainId: getChainId(),
						stationIds: placeId ? [placeId] : null,
						from: moment().subtract(30, 'd').startOf('day').toISOString(),
						to: moment().endOf('day').toISOString(),
					}}
					pagination={<ListPagination />}
					bulkActionButtons={false}
					titleOnPrint="Relatório de Transações Dotz - Abastecimento"
					filterTablePrint={<FilterTablePrint />}
					landscape
				>
					<DotzRefuelContent />
				</ReportList>
			</Permission>
		</CheckChainUsesModule>
	);
};

export default ReportDotzRefuelTransactions;
